import { inject, provide } from 'vue';
import type { ComputedRef, InjectionKey, Ref, ShallowRef } from 'vue';
import type { IUpdateReservationSettingsDto } from '@/api/services/reservations.service';
import type {
  TReservationGuideFinishState,
  TReservationGuideScreen,
} from '@/domain/reservations/reservation-guide';
import type { IGuestPreviewInput } from '@/domain/reservations/reservation-guest-preview';
import type { IReservationSettingsConfiguration } from '@/interfaces/IReservationSettings';
import type { IReservationFloorPlan } from './use-reservation-floor-plan';
import type { IReservationTablesDraft } from './use-reservation-tables-draft';

/**
 * The setup guide's shell owns every piece of state and provides this one
 * typed context; steps, the finish screen and the preview inject it and
 * emit nothing upwards (§4.2 of the guided-setup plan).
 */
export interface IReservationGuideScope {
  restaurantId: number;
  branchId: number;
}

export interface IReservationGuideAlertsDraft {
  email: string;
  tel: string;
  whatsapp: string;
}

export interface IReservationGuideExitTarget {
  tab: 'today' | 'settings';
  section?: string;
}

export interface IReservationGuideContext {
  scope: () => IReservationGuideScope | null;
  configuration: Readonly<ShallowRef<IReservationSettingsConfiguration | null>>;
  status: Readonly<Ref<'idle' | 'loading' | 'ready' | 'error'>>;
  screen: Readonly<Ref<TReservationGuideScreen>>;
  finishState: ComputedRef<TReservationGuideFinishState>;
  tablesDraft: IReservationTablesDraft;
  floorPlan: IReservationFloorPlan;
  /** Tables the name heuristic (D12) set to «No se usa»; listed in the undoable note. */
  excludedIds: Readonly<Ref<number[]>>;
  /** No ids = all. */
  clearExcluded(ids?: readonly number[]): void;
  /** Some table had no seats when the Mesas step first opened. */
  seatlessOnOpen: Readonly<Ref<boolean>>;
  /** The global «Todas las mesas» value; shared so undo can use it. */
  allSeats: Ref<number>;
  alertsDraft: Ref<IReservationGuideAlertsDraft>;
  alertsPrefill: Readonly<Ref<IReservationGuideAlertsDraft>>;
  /** alertsPrefill = { ...alertsDraft }, or only the given fields after a partial save. */
  commitAlerts(fields?: Partial<IReservationGuideAlertsDraft>): void;
  rulesEditing: Ref<boolean>;
  /** Computed from a pending counter, so overlapping writes never re-enable the UI early. */
  busy: Readonly<Ref<boolean>>;
  /** pending++ / current() / serverError / pending--; null when the task failed or the scope changed. */
  run<T>(task: () => Promise<T>, fallbackMessage: string): Promise<T | null>;
  /** Reloads the bundle inside `run`; false when the load failed or the scope changed. */
  reload(): Promise<boolean>;
  savePatch(patch: IUpdateReservationSettingsDto): Promise<boolean>;
  /** From 'done' remembers where to return. */
  go(screen: TReservationGuideScreen): void;
  /** Returns to 'done' when a return point is set. */
  next(): void;
  back(): void;
  /** Runs the leave guard; no target = remove `setup` only. */
  exit(target?: IReservationGuideExitTarget): Promise<void>;
}

/**
 * What a step exposes to the shell through its template ref (`defineExpose`).
 * Vue 2.7 unwraps exposed refs on the instance, so `ready` is read as a boolean.
 */
export interface IGuideStepHandle {
  ready: boolean;
  continue: () => Promise<void>;
  /** A CTA label other than «Continuar» (Mesas: create / blocked). */
  nextLabel?: string | null;
  /** A quiet secondary action in the bar (Grupos: «Por ahora no»). */
  secondaryLabel?: string | null;
  /** Rows the preview shows on top of the stored shifts (Turnos: the proposals). */
  previewShifts?: IGuestPreviewInput['shifts'];
}

export const RESERVATION_GUIDE_KEY: InjectionKey<IReservationGuideContext> = Symbol(
  'reservation-guide'
);

export function provideReservationGuide(
  context: IReservationGuideContext
): void {
  provide(RESERVATION_GUIDE_KEY, context);
}

/** Throws when no guide shell provides the context. */
export function useReservationGuide(): IReservationGuideContext {
  const context = inject(RESERVATION_GUIDE_KEY, null);
  if (!context)
    throw new Error(
      'useReservationGuide() must be called inside the reservations guide'
    );
  return context;
}
